import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ModeContext = createContext();

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
};

export const ModeProvider = ({ children }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState('business'); // 'business' or 'homeowner'

  // Load saved mode from localStorage on mount
  useEffect(() => {
    if (user && user.user_type === 'contractor') {
      const savedMode = localStorage.getItem('contractor_mode');
      if (savedMode) {
        setMode(savedMode);
      } else {
        // Default to business mode for contractors
        setMode('business');
      }
    }
  }, [user]);

  // Save mode to localStorage whenever it changes
  useEffect(() => {
    if (user && user.user_type === 'contractor') {
      localStorage.setItem('contractor_mode', mode);
    }
  }, [mode, user]);

  const toggleMode = () => {
    const newMode = mode === 'business' ? 'homeowner' : 'business';
    setMode(newMode);
  };

  const switchToBusinessMode = () => {
    setMode('business');
  };

  const switchToHomeownerMode = () => {
    setMode('homeowner');
  };

  // Contractors and admins can have dual mode. Admins legitimately need both
  // views for support/QA (e.g. walking a homeowner through a flow, debugging
  // a contractor's screen) without logging in/out of separate accounts. The
  // toggle is a view switcher only — it does not grant any new permissions.
  const isDualModeEnabled = user && (user.user_type === 'contractor' || user.is_admin === true);

  // Effective user_type for VIEW rendering. Admins follow the toggle so the
  // contractor/homeowner UI flips for them. Real users keep their own type.
  // Pages should use this for "what do I render" decisions and keep
  // user.user_type for "what data do I load" decisions (so the API isn't
  // asked for contractor data for an admin who isn't one).
  const effectiveUserType = user?.is_admin
    ? (mode === 'business' ? 'contractor' : 'homeowner')
    : user?.user_type;

  const value = {
    mode,
    setMode,
    toggleMode,
    switchToBusinessMode,
    switchToHomeownerMode,
    isDualModeEnabled,
    isBusinessMode: mode === 'business',
    isHomeownerMode: mode === 'homeowner',
    effectiveUserType,
    isContractorView: effectiveUserType === 'contractor',
    isHomeownerView: effectiveUserType === 'homeowner',
    isBusinessView: effectiveUserType === 'business',
  };

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};
